import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { LocationsService } from "../shared/locations.service";
import { StatesServices } from "../../shared/services/states/states.service";
import { ToastrService } from "ngx-toastr";
import { CanComponentDeactivate } from "../../shared/guards/confirmation/confirmation.guard";
import { LocationValidationService } from "../shared/locationValidation.service";
import { LocationViewModel } from "../../shared/models/locations/LocationViewModel.model";
import { authenticationService } from "@app/login/auth.service";
import { Permissions } from "@models/permissions.enum";
import { ProductQualificationService } from "@app/product-qualification/shared/product-qualification.service";
import { PcMilerService } from "../../pcMiler/shared/pcMiler.service";

@Component({
  selector: "app-location-detail",
  templateUrl: "./location-detail.component.html",
  styleUrls: ["./location-detail.component.css"],
})
export class LocationDetailComponent implements OnInit, CanComponentDeactivate {
  location: LocationViewModel = new LocationViewModel();
  isNewLocation: boolean = false;
  isEditMode: boolean = false;
  isProcessing: boolean = false;
  selectedTab: string = "details";
  hasUnsavedChanges: boolean = false;
  
  // Data
  originalInfo: any;
  countries = [
    { name: "CANADA", abbreviation: "CA" },
    { name: "MEXICO", abbreviation: "MX" },
    { name: "US", abbreviation: "US" },
  ];
  states: { abbreviation: string; fullName: string }[];
  productQualifications: any[] = [];
  loadingProductQualifications: boolean = true;
  exactLocationsData: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private locationService: LocationsService,
    private statesServices: StatesServices,
    private toastr: ToastrService,
    private locationValidationService: LocationValidationService,
    private authService: authenticationService,
    private productQualificationService: ProductQualificationService,
    private pcMilerService: PcMilerService
  ) {}

  ngOnInit() {
    this.states = this.statesServices.getTwoLettersStates("US");
    this.loadProductQualifications();
    
    this.route.params.subscribe((params) => {
      const locationId = params["locationId"];
      
      if (locationId === "newlocation") {
        this.initializeNewLocation();
      } else {
        this.loadExistingLocation(locationId);
      }
    });
  }

  loadProductQualifications() {
    this.productQualificationService.list(false).subscribe({
      next: (data) => {
        this.productQualifications = data || [];
        this.loadingProductQualifications = false;
      },
      error: () => {
        this.productQualifications = [];
        this.loadingProductQualifications = false;
      },
    });
  }

  initializeNewLocation() {
    this.isNewLocation = true;
    this.isEditMode = true;
    this.location = new LocationViewModel();
    this.location.countryAbbreviation = "US";
    this.location.country = "US";
    this.location.isShipper = false;
    this.location.isConsignee = false;
    this.location.isBillTo = false;
    this.location.productQualifications = [];
  }

  loadExistingLocation(locationId: string) {
    this.isNewLocation = false;
    this.isEditMode = false;
    
    this.locationService.getLocationById(locationId).subscribe((result: any) => {
      this.location = result || new LocationViewModel();
      this.originalInfo = JSON.stringify(this.location);
      
      // Load states for the location's country
      if (this.location.countryAbbreviation) {
        this.states = this.statesServices.getTwoLettersStates(this.location.countryAbbreviation);
      }
    });
  }

  hasPermission(permissionName: string): boolean {
    const permission = Permissions[permissionName];
    return this.authService.hasPermission(permission);
  }

  onTabClicked(tab: string) {
    this.selectedTab = tab;
  }

  onTabHasChanges(hasChanges: boolean) {
    this.hasUnsavedChanges = hasChanges;
  }

  toggleEditMode() {
    if (!this.isEditMode) {
      // Entering edit mode
      this.isEditMode = true;
      this.originalInfo = JSON.stringify(this.location);
    } else {
      // Saving changes
      this.saveLocation();
    }
  }

  cancelEdit() {
    if (this.isNewLocation) {
      this.router.navigate(["/locations"]);
    } else {
      this.isEditMode = false;
      this.resetChangesFlag();
      this.location = JSON.parse(this.originalInfo);
      
      // Reload states for the location's country
      if (this.location.countryAbbreviation) {
        this.states = this.statesServices.getTwoLettersStates(this.location.countryAbbreviation);
      }
    }
  }

  saveLocation() {
    if (!this.validateLocation(this.location)) {
      return;
    }

    this.isProcessing = true;

    if (this.isNewLocation) {
      // For new locations, show the near locations dialog
      this.openSelectExactAddressDialog(this.location);
    } else {
      // Check if address fields have changed
      const originalLocation = JSON.parse(this.originalInfo);
      const addressFields = [
        "address",
        "city",
        "state",
        "stateAbbreviation",
        "zipcode",
        "country",
        "countryAbbreviation",
      ];

      const isAddressChanged = addressFields.some((p) => this.location[p] !== originalLocation[p]);

      if (!isAddressChanged) {
        // No address changes, update directly
        this.updateExistingLocation();
        return;
      }

      // Address changed - show address picker modal
      if (this.location.confidenceLevel !== "MANUAL") {
        this.openSelectExactAddressDialog(this.location);
        return;
      }

      // Manual lat/long set - ask user
      if (
        confirm(
          "This address has a manual lat/long set. " +
            "Do you want to set the lat/long from PC Miler?"
        )
      ) {
        this.openSelectExactAddressDialog(this.location);
      } else {
        this.updateExistingLocation();
      }
    }
  }

  createNewLocation() {
    this.locationService.createLocation(this.location).subscribe(
      (locationCreated: any) => {
        this.toastr.success("Location created", "Success");
        this.isProcessing = false;
        this.isNewLocation = false; // Mark as no longer a new location
        this.isEditMode = false; // Exit edit mode
        this.resetChangesFlag();
        this.router.navigate([`/locations/${locationCreated.id}`]);
      },
      (errors) => {
        this.handleErrors(errors);
        this.isProcessing = false;
      }
    );
  }

  async openSelectExactAddressDialog(location: any) {
    try {
      /*
      The dialog will open automatically when `exactLocationsData` is populated then
      later call `finishLocationCreateUpdate` below to finish the create/update process.
      */
      const result = await this.pcMilerService.geoCodeLocation(location);
      this.exactLocationsData = Array.isArray(result) ? result : [];
    } catch (error: any) {
      this.isProcessing = false;
      if (error.responseText === "Input postal code doesn't match input state") {
        this.toastr.error("ZIP and State don't match");
      } else {
        try {
          const errors = JSON.parse(error.responseText);
          if (!errors.length) {
            this.toastr.error("An error occurred when trying to geocode the location.");
          } else if (errors[0]["Code"] === 25) {
            this.toastr.error("You must provide a City or ZIP.");
          } else {
            const message = errors[0]["Description"];
            this.toastr.error(message, "Error");
          }
        } catch (err) {
          this.toastr.error(error.responseText || "An error occurred", "Error");
        }
      }
    }
  }

  finishLocationCreateUpdate(exactLocationData: any) {
    // Update location with selected PC Miler data
    this.location.address = exactLocationData.Address.StreetAddress;
    this.location.city = exactLocationData.Address.City;
    this.location.state = exactLocationData.Address.State;
    this.location.stateAbbreviation = exactLocationData.Address.StateAbbreviation;
    if (exactLocationData.Address.Zip) {
      this.location.zipcode = exactLocationData.Address.Zip;
    }
    this.location.country = exactLocationData.Address.Country;
    this.location.countryAbbreviation = exactLocationData.Address.CountryAbbreviation;
    this.location.latitude = exactLocationData.Coords.Lat;
    this.location.longitude = exactLocationData.Coords.Lon;
    this.location.confidenceLevel = exactLocationData.ConfidenceLevel;
    // Note: numMatches is not part of LocationViewModel but may be used by the service
    this.location.IsUserChosen = true;

    // Clear the dialog data
    this.exactLocationsData = [];

    // Create or update based on whether it's a new location
    if (this.isNewLocation) {
      this.createNewLocation();
    } else {
      this.updateExistingLocation();
    }
  }

  updateExistingLocation() {
    this.locationService.update(this.location).subscribe({
      next: () => {
        this.toastr.success("Location updated", "Success");
        this.isProcessing = false;
        this.isEditMode = false;
        this.resetChangesFlag();
        this.originalInfo = JSON.stringify(this.location);
      },
      error: (errors) => {
        this.handleErrors(errors);
        this.isProcessing = false;
      },
    });
  }

  validateLocation(location: LocationViewModel): boolean {
    let errors = this.locationValidationService.validate(location);
    if (errors && errors.length > 0) {
      for (const error of errors) {
        this.toastr.warning(error, "Alert!");
      }
      return false;
    }
    return true;
  }

  handleErrors(errors: any) {
    if (Array.isArray(errors)) {
      for (const error of errors) {
        this.toastr.warning(error, "Alert!", {
          closeButton: true,
          enableHtml: true,
        });
      }
    } else {
      this.toastr.error(errors, "Error");
    }
  }

  // Country change handler
  onCountryChange() {
    if (this.location.countryAbbreviation) {
      this.states = this.statesServices.getTwoLettersStates(this.location.countryAbbreviation);
      // Reset state if it doesn't match the new country
      const stateExists = this.states.find(s => s.abbreviation === this.location.stateAbbreviation);
      if (!stateExists) {
        this.location.stateAbbreviation = null;
      }
    }
  }

  // Auto-generate location ID
  updateLocationId() {
    if (this.location.name && this.location.city && this.location.stateAbbreviation) {
      this.location.locId = this.locationService.generateLocationId(
        this.location.name,
        this.location.city,
        this.location.stateAbbreviation
      );
    }
  }

  // Guard
  confirm(): boolean {
    if (this.hasUnsavedChanges || (this.isEditMode && !this.isNewLocation)) {
      return window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
    } else if (this.isNewLocation) {
      return window.confirm("Leave this page without creating the location?");
    }
    return true;
  }

  resetChangesFlag() {
    this.hasUnsavedChanges = false;
  }
}

